import React, { useState, useMemo } from "react";
import {
  calcSD as _calcSD,
  calcLCT as _calcLCT,
  isEvFbtExempt,
  incomeTax,
  medicareLevy,
  getResidualByTermAndKm,
  FBT_STATUTORY,
  GST_PURCHASE_CREDIT_CAP,
  ONCOSTS_META,
} from './veercal.oncosts';
import { SiteHeader, SiteFooter } from './design/v2/SiteChrome';
import DepthControl from './results/DepthControl';
import ProgressiveCard from './results/ProgressiveCard';
import { buildQuickCompareItems } from './results/quickCompareBreakdown';

/* Design system v2 ("Tell-tale"): the v2 header and footer every page uses, and
   the vc- classes in /design/v2/site.css (linked in public/index.html). No icons
   are drawn (docs/brand/DESIGN.md §10; Pat, 22 Oct 2026), and nothing reads the
   colour calcResults puts on each result: figures are ink. */
const cx = (...names) => names.filter(Boolean).join(" ");

/* Wrappers matching the signatures used throughout QuickCompare.
   vType maps QuickCompare's (isEV, evType) inputs to oncosts.calcSD's vehicle-type
   domain ('ev' | 'phev' | 'new'). EV stamp-duty concessions (VIC green flat rate,
   QLD hybrid rate, ACT zero-emission rate, NT first $50k) only fire when this is set. */
export function qcVType(isEV, evType) {
  if (!isEV) return 'new';
  return evType === 'phev' ? 'phev' : 'ev';
}
export function calcStampDuty(price, state, vType = 'new') { return _calcSD(price, state, vType, false); }
export function calcLCT(price, isEV)        { return _calcLCT(price, isEV); }

/* ─── Local prefills ─────────────────────────────────────────────────────────
   ALL regulatory rates (FBT, tax brackets, Medicare, LCT, residuals, stamp
   duty, EV concessions) are imported from veercal.oncosts.js — the single
   source of truth. The block below holds only scenario prefills (benchmark
   finance rates + default running cost assumptions). These vary with market
   conditions, not law, so they live with the component.
────────────────────────────────────────────────────────────────────────────── */
const QC_PREFILLS = {
  /* Default running cost assumptions */
  defaults: {
    fuelPerL: 2.10, fuelL100km: 9, evEffKwh: 18, evChargeRate: 0.28,
    evPctHomeCharge: 0.80, evPublicChargeKwh: 0.60,
    tyres: 600, service: 800, insurance: 1800, rego: 900,
    dep1: 0.15, dep2: 0.12, depN: 0.10, opportunityCost: 0.05,
  },
  /* Benchmark finance rates — indicative market rates as of ONCOSTS_META.lastReviewed */
  rates: { personalLoan: 0.0899, dealerFinance: 0.0699, novatedLease: 0.0650 },
};

/* incomeTax, medicareLevy, getResidualByTermAndKm, isEvFbtExempt are all
   imported from veercal.oncosts.js — single source of truth. */
const qcTotalTax = (salary) => incomeTax(salary) + medicareLevy(salary);

/**
 * QuickCompare — Veercal Guided Flow Calculator
 * ─────────────────────────────────────────────────────────────────────────────
 * A card-based step-through experience that collects inputs across 5 steps
 * and presents a simplified comparison result with a link to the full calculator.
 *
 * Steps:
 *   1. Your vehicle       — price, state, EV toggle
 *   2. How you'll use it  — annual km, hold period
 *   3. Finance type       — which structures to compare
 *   4. Your details       — salary (if novated), deposit, loan rate
 *   5. Results            — side-by-side comparison cards
 *
 * © 2025 Veercal Pty. Ltd. All rights reserved.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/* Tokens (colour, type, spacing) live in src/design/tokens.js. */

/* ─── Finance maths (self-contained, uses RATES config) ─────────────────── */
export function pmt(rate, nper, pv, fv = 0) {
  const r = rate / 12;
  if (r === 0) return (pv + fv) / nper;
  return (r * (pv * Math.pow(1 + r, nper) + fv)) / (Math.pow(1 + r, nper) - 1);
}

export function vehVal(price, yrs, dep1, dep2, depN) {
  let v = price;
  for (let y = 1; y <= yrs; y++) {
    v *= 1 - (y === 1 ? dep1 : y === 2 ? dep2 : depN);
  }
  return Math.max(v, price * 0.05);
}

export function runningCost(inputs) {
  const { annualKm, isEV, fuelPerL, fuelL100km, evEffKwh, evChargeRate,
          evPctHomeCharge, evPublicChargeKwh,
          tyres, service, insurance, rego } = inputs;
  let fuel;
  if (isEV) {
    /* Blend home + public DC charging. Falls back to 100% home charging if
       the new fields aren't present (older shared scenarios). */
    const totalKwh = (annualKm / 100) * evEffKwh;
    const homePct = evPctHomeCharge != null ? evPctHomeCharge : 1;
    const publicRate = evPublicChargeKwh != null ? evPublicChargeKwh : evChargeRate;
    fuel = totalKwh * homePct * evChargeRate + totalKwh * (1 - homePct) * publicRate;
  } else {
    fuel = (annualKm / 100) * fuelL100km * fuelPerL;
  }
  /* EVs have ~40% lower servicing costs — matches full calculator */
  const effectiveService = isEV ? service * 0.6 : service;
  return fuel + tyres + effectiveService + insurance + rego;
}

export function calcResults(inputs) {
  const {
    vehiclePrice, state, isEV, evType, holdYears, annualKm,
    deposit, loanRate, loanTerm, salary, novatedRate, novatedTerm,
    fuelPerL, fuelL100km, evEffKwh, evChargeRate,
    tyres, service, insurance, rego,
    dep1, dep2, depN, opportunityCost,
    includeNovated, includeLoan, includeCash, includeDealer, dealerRate, dealerTerm, dealerDeposit = 2000,
    financeStampDuty = false,
    includeNovatedGstOnPurchase = true,
  } = inputs;

  /* ── Drive-away → base price solver ──────────────────────────────────────
     Drive-away prices include BOTH stamp duty AND LCT (LCT is charged by the
     manufacturer and baked into advertised drive-away prices).
     Solve: driveAway = basePrice + stampDuty(basePrice) + LCT(basePrice)
  ─────────────────────────────────────────────────────────────────────────── */
  const driveAway = vehiclePrice;
  const vType = qcVType(isEV, evType);
  /* Iteratively solve drive-away = base + SD(base+LCT) + LCT(base). vType
     lets the SD calc fire NSW/ACT EV exemptions + VIC green-car flat rate.
     Typically converges in 3-5 iterations. */
  let basePrice = driveAway * 0.88; /* initial estimate — leaves room for SD+LCT */
  for (let i = 0; i < 20; i++) {
    const next = driveAway - calcLCT(basePrice, isEV) - calcStampDuty(basePrice + calcLCT(basePrice, isEV), state, vType);
    if (Math.abs(next - basePrice) < 0.5) { basePrice = next; break; }
    basePrice = next;
  }
  basePrice = Math.round(basePrice);

  /* Stamp duty and LCT are BOTH extracted from drive-away — neither is added on top */
  const lct = Math.round(calcLCT(basePrice, isEV));                        /* inside drive-away */
  const stampDuty = Math.round(calcStampDuty(basePrice + lct, state, vType));     /* SD on dutiable value = base + LCT */
  /* Effective purchase cost = drive-away (SD and LCT already inside) */
  const effectivePrice = driveAway;
  /* Asset value and depreciation track the base price (market value of the car) */
  const exitValue = vehVal(basePrice, holdYears, dep1, dep2, depN);
  const runPerYear = runningCost({ annualKm, isEV, fuelPerL, fuelL100km, evEffKwh, evChargeRate,
    evPctHomeCharge: inputs.evPctHomeCharge, evPublicChargeKwh: inputs.evPublicChargeKwh,
    tyres, service, insurance, rego });
  const totalRunning = runPerYear * holdYears;
  const results = [];

  /* Cash */
  if (includeCash) {
    const oppCost = effectivePrice * (Math.pow(1 + opportunityCost, holdYears) - 1);
    const totalCost = effectivePrice + totalRunning - exitValue + oppCost;
    results.push({
      method: "cash", label: "Cash Purchase", icon: "cash", color: "#166534",
      monthlyPayment: 0,
      totalCost: Math.round(totalCost),
      effectiveMonthly: Math.round(totalCost / (holdYears * 12)),
      upfront: Math.round(effectivePrice),
      totalInterest: 0,
      /* Unrounded cost components for the breakdown builder (display rounds
         via fmt). Summing unrounded keeps sum(items) within $1 of totalCost. */
      _components: { upfront: effectivePrice, running: totalRunning, opportunityCost: oppCost, exitValue },
      note: "Includes opportunity cost on capital deployed",
    });
  }

  /* Personal loan — base + LCT in drive-away; SD upfront by default (or
     rolled into the loan when financeStampDuty is true, M12). */
  if (includeLoan) {
    const financedSd = financeStampDuty ? stampDuty : 0;
    const upfrontSd  = financeStampDuty ? 0 : stampDuty;
    const financedAmount = Math.max(0, basePrice + lct + financedSd - deposit);
    const months = loanTerm * 12;
    const mp = pmt(loanRate, months, financedAmount);
    const holdMonths = Math.min(holdYears * 12, months);
    const totalPaid = deposit + upfrontSd + holdMonths * mp;
    const totalInt = mp * months - financedAmount;
    /* Opp cost only on what's actually deployed upfront. */
    const oppCostLoan = (deposit + upfrontSd) * (Math.pow(1 + opportunityCost, holdYears) - 1);
    const totalCost = totalPaid + totalRunning - exitValue + oppCostLoan;
    results.push({
      method: "loan", label: "Personal Loan", icon: "loan", color: "#134E35",
      monthlyPayment: Math.round(mp),
      totalCost: Math.round(totalCost),
      effectiveMonthly: Math.round(totalCost / (holdYears * 12)),
      upfront: Math.round(deposit + upfrontSd),
      totalInterest: Math.round(totalInt),
      opportunityCostTotal: Math.round(oppCostLoan),
      _components: { deposit, upfrontSd, payments: holdMonths * mp, running: totalRunning, opportunityCost: oppCostLoan, exitValue },
      note: `${(loanRate * 100).toFixed(2)}% rate, ${loanTerm}-yr term${financeStampDuty ? ' · SD financed' : ''}`,
    });
  }

  /* Dealer finance — LCT financed; SD upfront by default (or rolled in via M12). */
  if (includeDealer) {
    const dep = dealerDeposit;
    const balloon = basePrice * 0.25;
    const financedSd = financeStampDuty ? stampDuty : 0;
    const upfrontSd  = financeStampDuty ? 0 : stampDuty;
    const financedAmount = Math.max(0, basePrice + lct + financedSd - dep);
    const months = dealerTerm * 12;
    const mp = pmt(dealerRate, months, financedAmount, -balloon);
    const holdMonths = Math.min(holdYears * 12, months);
    const totalPaid = dep + upfrontSd + holdMonths * mp;
    const balloonOwing = holdYears >= dealerTerm ? balloon : 0;
    const totalInt = mp * months - (financedAmount - balloon);
    const oppCostDealer = (dep + upfrontSd) * (Math.pow(1 + opportunityCost, holdYears) - 1);
    const totalCost = totalPaid + totalRunning - exitValue + Math.max(0, balloonOwing) + oppCostDealer;
    results.push({
      method: "dealer", label: "Dealer Finance", icon: "vehicle", color: "#ea580c",
      monthlyPayment: Math.round(mp),
      totalCost: Math.round(totalCost),
      effectiveMonthly: Math.round(totalCost / (holdYears * 12)),
      upfront: Math.round(dep + upfrontSd),
      totalInterest: Math.round(totalInt),
      balloonOwing: Math.round(balloonOwing),
      opportunityCostTotal: Math.round(oppCostDealer),
      _components: { deposit: dep, upfrontSd, payments: holdMonths * mp, running: totalRunning, balloonOwing, opportunityCost: oppCostDealer, exitValue },
      note: `25% balloon · $${dep.toLocaleString("en-AU")} deposit · ${(dealerRate * 100).toFixed(2)}% rate · ${dealerTerm}yr`,
    });
  }

  /* Novated lease — principal = drive-away (stamp duty + LCT financed); residual
     and FBT use the ATO "cost price" = base + LCT (excludes SD/rego/CTP). */
  if (includeNovated && salary > 0) {
    const residualPct = getResidualByTermAndKm(novatedTerm, annualKm);
    /* ATO cost price for FBT statutory and residual minimum: GST-inclusive
       purchase price including LCT and delivery, excluding rego/CTP
       (TD 2017/16). In Veercal's model: extracted base + LCT. */
    const fbtCostPrice = basePrice + lct;
    const residual = fbtCostPrice * residualPct;
    const months = novatedTerm * 12;
    /* Purchase GST credit — financier funds the lease ex-GST, capped at 1/11 of the
       ATO car limit. basePrice is GST-inclusive ex-LCT/SD/rego, GST portion = /11.
       Mirrors calcNovated (App.js) so /compare matches /calculator and /novated. */
    const purchaseGstCredit = includeNovatedGstOnPurchase
      ? Math.min(basePrice / 11, GST_PURCHASE_CREDIT_CAP)
      : 0;
    /* Principal: drive-away (SD and LCT financed) less any purchase GST credit */
    const principal = driveAway - purchaseGstCredit;
    const gf = pmt(novatedRate, months, principal, -residual);
    const runMo = runPerYear / 12;
    const preTax = gf + runMo;
    /* Tax saving: bracket method — more accurate than flat marginal rate.
       Critical at salaries where sacrifice crosses bracket boundaries (e.g. $150k+). */
    const annualSacrifice = preTax * 12;
    const taxSavingAnnual = qcTotalTax(salary) - qcTotalTax(Math.max(0, salary - annualSacrifice));
    const taxSaving = taxSavingAnnual / 12;
    const effectiveTaxRate = taxSavingAnnual / annualSacrifice; /* effective rate for display */
    const netMo = preTax - taxSaving;
    /* FBT exemption test — centralised in oncosts.js. Uses drive-away + vType
       (same vType passed to calcSD) so the answer matches /calculator and
       /novated for the same scenario. Internally extracts base and checks if
       LCT would have been payable. */
    const evExempt = isEvFbtExempt(driveAway, vType, state);
    /* FBT: ECM employee contribution = fbtCostPrice × FBT_STATUTORY / 12.
       Cost price includes LCT per ATO TD 2017/16. */
    const fbtMo = evExempt ? 0 : (fbtCostPrice * FBT_STATUTORY) / 12;
    /* GST input credit: providers claim back GST on packaged running costs and
       credit it to the employee. Applies to GST-inclusive items (fuel/charging,
       insurance, servicing, tyres) — NOT registration. 1/11th of GST-inclusive. */
    const gstApplicableAnnual = Math.max(0, runPerYear - rego);
    const gstCreditMo = gstApplicableAnnual / 11 / 12;
    const trueNetMo = netMo + fbtMo - gstCreditMo;
    /* Phase 1: lease payments; Phase 2: post-lease running only; + residual buyout */
    const postLeaseYears = Math.max(0, holdYears - novatedTerm);
    const totalCost = trueNetMo * months + residual + runPerYear * postLeaseYears - exitValue;
    results.push({
      method: "novated", label: "Novated Lease", icon: "novated", color: "#b45309",
      monthlyPayment: Math.round(trueNetMo),
      totalCost: Math.round(totalCost),
      effectiveMonthly: Math.round(totalCost / (holdYears * 12)),
      upfront: 0,
      totalInterest: 0,
      taxSavingMonthly: Math.round(taxSaving),
      fbtExempt: evExempt,
      residual: Math.round(residual),
      opportunityCostTotal: 0,
      _components: { finance: gf * months, leaseRunning: runMo * months, taxSaving: taxSaving * months, fbt: fbtMo * months, gstCredit: gstCreditMo * months, residual, postLeaseRunning: runPerYear * postLeaseYears, exitValue, principal, purchaseGstCredit, driveAway, months },
      note: evExempt
        ? `FBT exempt (EV) · effective tax saving rate ${(effectiveTaxRate * 100).toFixed(1)}%`
        : `FBT applies · effective tax saving rate ${(effectiveTaxRate * 100).toFixed(1)}%`,
    });
  }

  /* Sort by total cost */
  results.sort((a, b) => a.totalCost - b.totalCost);
  return { results, stampDuty, lct, exitValue, runPerYear, basePrice };
}

/* ─── Formatters ─────────────────────────────────────────────────────────── */
const fmt = (n) => n == null ? "—"
  : (n < 0 ? "-$" : "$") + Math.abs(Math.round(n)).toLocaleString("en-AU");

/* ─── Step definitions ───────────────────────────────────────────────────── */
const STEPS = [
  { id: "vehicle",  title: "Your vehicle",        icon: "vehicle", desc: "Tell us what you are buying" },
  { id: "usage",    title: "How you will use it",  icon: "usage", desc: "Distance and how long you will keep it" },
  { id: "finance",  title: "Finance options",      icon: "finance", desc: "Which structures do you want to compare?" },
  { id: "details",  title: "Your details",         icon: "details", desc: "A few more details for an accurate comparison" },
  { id: "results",  title: "Your comparison",      icon: "results", desc: "See the true cost of each option" },
];

/* ─── Initial state ──────────────────────────────────────────────────────── */
export const INITIAL = {
  vehiclePrice: 55000,
  state: "VIC",

  isEV: false,
  evType: "bev",
  annualKm: 15000,
  holdYears: 5,
  includeCash: true,
  includeLoan: true,
  includeDealer: true,
  includeNovated: false,
  includeNovatedGstOnPurchase: true,
  financeStampDuty: false,
  deposit: 5000,
  dealerDeposit: 2000,
  loanRate: QC_PREFILLS.rates.personalLoan,
  loanTerm: 5,
  dealerRate: QC_PREFILLS.rates.dealerFinance,
  dealerTerm: 5,
  salary: 100000,
  novatedRate: QC_PREFILLS.rates.novatedLease,
  novatedTerm: 3,
  fuelPerL: QC_PREFILLS.defaults.fuelPerL,
  fuelL100km: QC_PREFILLS.defaults.fuelL100km,
  evEffKwh: QC_PREFILLS.defaults.evEffKwh,
  evChargeRate: QC_PREFILLS.defaults.evChargeRate,
  evPctHomeCharge: QC_PREFILLS.defaults.evPctHomeCharge,
  evPublicChargeKwh: QC_PREFILLS.defaults.evPublicChargeKwh,
  tyres: QC_PREFILLS.defaults.tyres,
  service: QC_PREFILLS.defaults.service,
  insurance: QC_PREFILLS.defaults.insurance,
  rego: QC_PREFILLS.defaults.rego,
  dep1: QC_PREFILLS.defaults.dep1,
  dep2: QC_PREFILLS.defaults.dep2,
  depN: QC_PREFILLS.defaults.depN,
  opportunityCost: QC_PREFILLS.defaults.opportunityCost,
};

/* ─── Reusable input components ──────────────────────────────────────────── */
/* A field's label and hint, shared with the control inside it (a number input or
   a group of choices) so each control is labelled by its field. */
const FieldCtx = React.createContext(null);

function Field({ label, hint, children }) {
  const id = React.useId();
  const ctx = { labelId: `${id}-label`, hintId: hint ? `${id}-hint` : undefined };
  return (
    <div className="vc-field">
      <span className="vc-field__label" id={ctx.labelId}>{label}</span>
      <FieldCtx.Provider value={ctx}>{children}</FieldCtx.Provider>
      {hint && <span className="vc-field__hint" id={ctx.hintId}>{hint}</span>}
    </div>
  );
}

function NumberInput({ value, onChange, prefix = "$", min, max }) {
  /* Use a text input so users can freely type — converts to number on blur.
     Avoids the browser stepping/validation behaviour of type="number"
     which prevents typing mid-number on some browsers. */
  const [local, setLocal] = React.useState(String(value));
  const field = React.useContext(FieldCtx);

  React.useEffect(() => {
    /* Sync if parent value changes externally (e.g. reset) */
    setLocal(String(value));
  }, [value]);

  const commit = () => {
    const parsed = parseFloat(local.replace(/[^0-9.]/g, ""));
    if (!isNaN(parsed)) {
      const clamped = min != null && parsed < min ? min
                    : max != null && parsed > max ? max
                    : parsed;
      onChange(clamped);
      setLocal(String(clamped));
    } else {
      setLocal(String(value)); /* revert if invalid */
    }
  };

  /* "$" goes before the figure; "%" after it, as on the other calculators. */
  const after = prefix === "%";
  return (
    <div className="vc-input">
      {prefix && !after && <span className="vc-input__affix">{prefix}</span>}
      <input
        type="text"
        inputMode={after ? "decimal" : "numeric"}
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => e.key === "Enter" && commit()}
        aria-labelledby={field ? field.labelId : undefined}
        aria-describedby={field ? field.hintId : undefined}
      />
      {after && <span className="vc-input__affix">{prefix}</span>}
    </div>
  );
}

/* A group of choices (segmented control), labelled by its field. */
function Choices({ children }) {
  const field = React.useContext(FieldCtx);
  return (
    <div className="vc-seg" role="group"
      aria-labelledby={field ? field.labelId : undefined} aria-describedby={field ? field.hintId : undefined}>
      {children}
    </div>
  );
}

function OptionPill({ label, selected, onClick }) {
  return (
    <button type="button" aria-pressed={!!selected} onClick={onClick}>
      {label}
    </button>
  );
}

function Toggle({ label, value, onChange, sub }) {
  const id = React.useId();
  return (
    <div className="vc-check">
      <input type="checkbox" id={id} checked={!!value} onChange={(e) => onChange(e.target.checked)}
        aria-describedby={sub ? `${id}-hint` : undefined} />
      <div className="vc-check__body">
        <label htmlFor={id}>{label}</label>
        {sub && <span className="vc-field__hint" id={`${id}-hint`}>{sub}</span>}
      </div>
    </div>
  );
}

/* A figure with its label: a reading with no gauge. */
function Stat({ label, value }) {
  return (
    <div className="vc-reading">
      <div className="vc-reading__body">
        <div className="vc-reading__label">{label}</div>
        <div className="vc-reading__value" style={{ fontSize: 20 }}>{value}</div>
      </div>
    </div>
  );
}

/* ─── Step components ────────────────────────────────────────────────────── */
function StepVehicle({ inputs, upd }) {
  const states = ["VIC","NSW","QLD","WA","SA","TAS","ACT","NT"];
  return (
    <>
      <Field label="Vehicle price (drive-away)" hint="As advertised — stamp duty is already included in drive-away prices. We'll extract it automatically.">
        <NumberInput value={inputs.vehiclePrice} onChange={(v) => upd("vehiclePrice", Math.max(1000, v))} />
      </Field>

      <Field label="State or territory">
        <Choices>
          {states.map(s => (
            <OptionPill key={s} label={s} selected={inputs.state === s} onClick={() => upd("state", s)} />
          ))}
        </Choices>
      </Field>

      <Field label="Vehicle type">
        <Choices>
          <OptionPill label="Petrol / Diesel" selected={!inputs.isEV} onClick={() => upd("isEV", false)} />
          <OptionPill label="Electric / EV" selected={inputs.isEV && inputs.evType === "bev"} onClick={() => { upd("isEV", true); upd("evType", "bev"); }} />
          <OptionPill label="Plug-in Hybrid" selected={inputs.isEV && inputs.evType === "phev"} onClick={() => { upd("isEV", true); upd("evType", "phev"); }} />
        </Choices>
        {inputs.isEV && inputs.evType === "phev" && (
          <div className="vc-aside">
            <p className="vc-aside__text">
              PHEV FBT exemption ended for <strong>new agreements</strong> from 1 April 2025. Existing leases in use before that date stay exempt until the lease ends, unless it's changed or extended. New PHEV agreements are not modelled as FBT-exempt here.
            </p>
          </div>
        )}
      </Field>

      {/* Live on-costs breakdown — both SD and LCT extracted from drive-away */}
      {(() => {
        const previewVType = qcVType(inputs.isEV, inputs.evType);
        let previewBase = inputs.vehiclePrice * 0.88;
        for (let i = 0; i < 20; i++) {
          const next = inputs.vehiclePrice - calcLCT(previewBase, inputs.isEV) - calcStampDuty(previewBase + calcLCT(previewBase, inputs.isEV), inputs.state, previewVType);
          if (Math.abs(next - previewBase) < 0.5) { previewBase = next; break; }
          previewBase = next;
        }
        previewBase = Math.round(previewBase);
        const previewLCT = Math.round(calcLCT(previewBase, inputs.isEV));
        const previewSD  = Math.round(calcStampDuty(previewBase + previewLCT, inputs.state, previewVType));
        return (
          <div className="vc-aside">
            <p className="vc-aside__text">
              <strong>Inside your drive-away price ({inputs.state}):</strong>{" "}
              Stamp duty {fmt(previewSD)}
              {previewLCT > 0 && (
                <span> · LCT {fmt(previewLCT)}</span>
              )}
              <span className="vc-small"> — already included, nothing added on top</span>
            </p>
          </div>
        );
      })()}
    </>
  );
}

function StepUsage({ inputs, upd }) {
  const kmOptions = [5000, 10000, 15000, 20000, 25000, 30000];
  const holdOptions = [2, 3, 4, 5, 7, 10];
  return (
    <>
      <Field label="Annual kilometres" hint="How far do you drive per year?">
        <Choices>
          {kmOptions.map(k => (
            <OptionPill key={k} label={`${(k/1000).toFixed(0)}k km`} selected={inputs.annualKm === k} onClick={() => upd("annualKm", k)} />
          ))}
        </Choices>
      </Field>

      <Field label="How long will you keep the car?" hint="This sets the comparison period for all finance options">
        <Choices>
          {holdOptions.map(y => (
            <OptionPill key={y} label={`${y} years`} selected={inputs.holdYears === y} onClick={() => upd("holdYears", y)} />
          ))}
        </Choices>
      </Field>

      {/* Running costs summary */}
      <div className="vc-aside">
        <p className="vc-aside__title">Estimated annual running costs</p>
        <table className="vc-table vc-table--compact">
          <tbody>
            {[
              { label: "Fuel / charging", value: (() => {
                  if (!inputs.isEV) return fmt((inputs.annualKm / 100) * inputs.fuelL100km * inputs.fuelPerL);
                  const totalKwh = (inputs.annualKm / 100) * inputs.evEffKwh;
                  const homePct = inputs.evPctHomeCharge != null ? inputs.evPctHomeCharge : 1;
                  const publicRate = inputs.evPublicChargeKwh != null ? inputs.evPublicChargeKwh : inputs.evChargeRate;
                  return fmt(totalKwh * homePct * inputs.evChargeRate + totalKwh * (1 - homePct) * publicRate);
                })() },
              { label: "Insurance",       value: fmt(inputs.insurance) },
              { label: "Registration",    value: fmt(inputs.rego) },
              { label: "Servicing",       value: fmt(inputs.isEV ? inputs.service * 0.6 : inputs.service) },
              { label: "Tyres",           value: fmt(inputs.tyres) },
            ].map(r => (
              <tr key={r.label}><th scope="row">{r.label}</th><td>{r.value}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function StepFinance({ inputs, upd }) {
  return (
    <>
      <p className="vc-small">Select which finance structures you want to compare. You can compare as many as you like.</p>

      <Toggle label="Cash purchase" value={inputs.includeCash} onChange={(v) => upd("includeCash", v)} />
      <Toggle label="Personal loan" value={inputs.includeLoan} onChange={(v) => upd("includeLoan", v)} />
      <Toggle label="Dealer finance" value={inputs.includeDealer} onChange={(v) => upd("includeDealer", v)} />
      <Toggle label="Novated lease (PAYG employees only)" value={inputs.includeNovated} onChange={(v) => upd("includeNovated", v)} />

      {inputs.includeNovated && (
        <div className="vc-aside">
          <p className="vc-aside__text">
            Novated leasing is available to permanent and long-term PAYG employees only. Your employer must offer salary packaging.
          </p>
        </div>
      )}

      {!inputs.includeCash && !inputs.includeLoan && !inputs.includeDealer && !inputs.includeNovated && (
        <p className="vc-error" role="alert">
          Select at least one option to compare.
        </p>
      )}
    </>
  );
}

function StepDetails({ inputs, upd }) {
  return (
    <>
      {(inputs.includeLoan || inputs.includeDealer) && (
        <Toggle label="Finance stamp duty into loan" value={!!inputs.financeStampDuty} onChange={(v) => upd("financeStampDuty", v)} />
      )}
      {inputs.includeLoan && (
        <>
          <Field label="Deposit (personal loan)" hint="Amount you will pay upfront">
            <NumberInput value={inputs.deposit} onChange={(v) => upd("deposit", v)} min={0} />
          </Field>
          <Field label="Loan interest rate" hint="Use the comparison rate from your lender">
            <NumberInput value={Math.round(inputs.loanRate * 10000) / 100} onChange={(v) => upd("loanRate", v / 100)} prefix="%" min={0} max={50} step={0.01} />
          </Field>
          <Field label="Loan term">
            <Choices>
              {[3, 4, 5, 6, 7].map(y => (
                <OptionPill key={y} label={`${y}yr`} selected={inputs.loanTerm === y} onClick={() => upd("loanTerm", y)} />
              ))}
            </Choices>
          </Field>
        </>
      )}

      {inputs.includeDealer && (
        <>
          <Field label="Dealer deposit" hint="Amount you will pay upfront to the dealer">
            <NumberInput value={inputs.dealerDeposit} onChange={(v) => upd("dealerDeposit", v)} min={0} />
          </Field>
          <Field label="Dealer finance rate" hint="Ask the dealer for their comparison rate">
            <NumberInput value={Math.round(inputs.dealerRate * 10000) / 100} onChange={(v) => upd("dealerRate", v / 100)} prefix="%" min={0} max={50} step={0.01} />
          </Field>
          <Field label="Dealer finance term">
            <Choices>
              {[3, 4, 5].map(y => (
                <OptionPill key={y} label={`${y}yr`} selected={inputs.dealerTerm === y} onClick={() => upd("dealerTerm", y)} />
              ))}
            </Choices>
          </Field>
        </>
      )}

      {inputs.includeNovated && (
        <>
          <Field label="Gross annual salary" hint="Your total pre-tax salary including super">
            <NumberInput value={inputs.salary} onChange={(v) => upd("salary", v)} min={0} />
          </Field>
          <Field label="Novated lease term">
            <Choices>
              {[2, 3, 4, 5].map(y => (
                <OptionPill key={y} label={`${y}yr`} selected={inputs.novatedTerm === y} onClick={() => upd("novatedTerm", y)} />
              ))}
            </Choices>
          </Field>
          <Toggle label="Provider passes on GST credit on purchase" value={inputs.includeNovatedGstOnPurchase} onChange={(v) => upd("includeNovatedGstOnPurchase", v)}
            sub="Most providers finance the car ex-GST — the financier claims the GST input credit on the purchase (capped at $6,353). Turn off if it isn't passed on." />
          {isEvFbtExempt(inputs.vehiclePrice, qcVType(inputs.isEV, inputs.evType), inputs.state) && (
            <span className="vc-chip vc-chip--good" style={{ alignSelf: "flex-start" }}>
              This EV is FBT-exempt under a novated lease — no post-tax FBT contribution required.
            </span>
          )}
        </>
      )}

      {inputs.includeCash && (
        <Field label="Opportunity cost rate" hint="Expected annual return if you invested the money instead (e.g. 5% for a balanced portfolio, 6% if you have a mortgage at that rate)">
          <NumberInput value={Math.round(inputs.opportunityCost * 1000) / 10} onChange={(v) => upd("opportunityCost", v / 100)} prefix="%" min={0} max={25} step={0.5} />
        </Field>
      )}
    </>
  );
}

/* ─── Insight flip cards ─────────────────────────────────────────────────── */
function InsightCards({ results, inputs }) {
  const [flipped, setFlipped] = React.useState({});
  const flip = (id) => setFlipped(p => ({ ...p, [id]: !p[id] }));

  const lowestMonthly = [...results].sort((a, b) => a.monthlyPayment - b.monthlyPayment)[0];
  const lowestTotal   = results[0]; /* already sorted by totalCost */
  const novated       = results.find(r => r.method === "novated");
  const loan          = results.find(r => r.method === "loan");
  const cash          = results.find(r => r.method === "cash");
// eslint-disable-next-line
  const dealer        = results.find(r => r.method === "dealer");

  const cards = [];

  /* Card 1: lowest monthly payment */
  if (lowestMonthly) {
    cards.push({
      id: "monthly",
      front_title: "Lowest monthly payment",
      front_value: fmt(lowestMonthly.monthlyPayment || 0) + "/mo",
      front_label: lowestMonthly.label,
      back_title: "Why this is lowest",
      back_body: lowestMonthly.method === "cash"
        ? `Paying cash means no monthly finance repayment at all — your only ongoing costs are running expenses of ${fmt(Math.round(inputs.annualKm / 100 * inputs.fuelL100km * inputs.fuelPerL / 12 + (inputs.tyres + inputs.service + inputs.insurance + inputs.rego) / 12))}/mo. However the true total cost includes the full purchase price upfront.`
        : lowestMonthly.method === "dealer"
        ? `Dealer finance with a 25% balloon keeps monthly payments low because you are not paying down the full loan balance — ${fmt(lowestMonthly.balloonOwing || Math.round(inputs.vehiclePrice * 0.25))} remains owing at the end of the term.`
        : lowestMonthly.method === "novated"
        ? `Salary sacrifice reduces your effective monthly out-of-pocket to ${fmt(lowestMonthly.monthlyPayment)}/mo. You are saving ${fmt(lowestMonthly.taxSavingMonthly)}/mo in income tax${lowestMonthly.fbtExempt ? " and paying no FBT (EV exemption applies)" : ""}.`
        : `A ${inputs.loanTerm}-year loan at ${(inputs.loanRate * 100).toFixed(2)}% spreads the cost over ${inputs.loanTerm * 12} monthly payments of ${fmt(lowestMonthly.monthlyPayment)}.`,
    });
  }

  /* Card 2: lowest true total cost */
  if (lowestTotal) {
    const secondCheapest = results[1];
    cards.push({
      id: "total",
      front_title: "Lowest true total cost",
      front_value: fmt(lowestTotal.totalCost),
      front_label: `over ${inputs.holdYears} years — ${lowestTotal.label}`,
      back_title: "What makes it cheapest",
      back_body: secondCheapest
        ? `${lowestTotal.label} costs ${fmt(lowestTotal.totalCost)} over ${inputs.holdYears} years — that is ${fmt(secondCheapest.totalCost - lowestTotal.totalCost)} less than ${secondCheapest.label} (${fmt(secondCheapest.totalCost)}). True total cost includes all finance, running costs, stamp duty, and exit value.`
        : `${lowestTotal.label} is the only option selected. Add more options in Step 3 to compare.`,
    });
  }

  /* Card 3: novated tax saving (if applicable) */
  if (novated) {
    cards.push({
      id: "novated",
      front_title: "Novated tax saving",
      front_value: fmt(novated.taxSavingMonthly * 12) + "/yr",
      front_label: `${fmt(novated.taxSavingMonthly)}/mo income tax saved`,
      back_title: "How your novated saving works",
      back_body: `On a ${fmt(inputs.salary)} salary, your marginal rate is approximately ${Math.round((novated.taxSavingMonthly / ((novated.monthlyPayment + novated.taxSavingMonthly))) * 100)}%. Packaging ${fmt(novated.monthlyPayment + novated.taxSavingMonthly)}/mo pre-tax saves you ${fmt(novated.taxSavingMonthly)}/mo or ${fmt(novated.taxSavingMonthly * 12)}/yr in income tax.${novated.fbtExempt ? " Your EV is FBT-exempt so no post-tax contribution is required." : ""} Over ${inputs.holdYears} years that is ${fmt(novated.taxSavingMonthly * 12 * inputs.holdYears)} in total tax savings.`,
    });
  }

  /* Card 4: cash vs loan — interest cost insight */
  if (cash && loan) {
    const interestSaving = loan.totalInterest;
    cards.push({
      id: "cashvloan",
      front_title: "Interest cost — loan",
      front_value: fmt(interestSaving),
      front_label: "total interest on personal loan",
      back_title: "Cash vs loan trade-off",
      back_body: `Financing at ${(inputs.loanRate * 100).toFixed(2)}% costs ${fmt(interestSaving)} in interest over ${inputs.loanTerm} years. Cash avoids this — but the opportunity cost of not investing ${fmt(inputs.vehiclePrice)} adds approximately ${fmt(cash.opportunityCostTotal || Math.round(inputs.vehiclePrice * (Math.pow(1 + inputs.opportunityCost, inputs.holdYears) - 1)))} over ${inputs.holdYears} years at ${(inputs.opportunityCost * 100).toFixed(0)}% assumed return. ${interestSaving < (cash.opportunityCostTotal || 0) ? "In this scenario investing the cash and financing the car is mathematically cheaper." : "In this scenario cash is mathematically cheaper — the loan interest exceeds the opportunity cost."}`,
    });
  }

  if (cards.length === 0) return null;

  return (
    <div className="vc-calc__block">
      <p className="vc-label">Tap any card for insight</p>
      <div className="vc-flips">
        {cards.map(card => {
          const isFlipped = !!flipped[card.id];
          return (
            <div key={card.id}
              className={cx("vc-flip", isFlipped && "is-flipped")}
              role="button"
              tabIndex={0}
              aria-pressed={isFlipped}
              onClick={() => flip(card.id)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); flip(card.id); } }}
            >
              {/*
                Invisible spacer — renders the back content in normal flow so the
                container grows to fit it. Front and back are then absolutely
                positioned within that space. This prevents back text overflow.
              */}
              <div className="vc-flip__size" aria-hidden="true">
                <div className="vc-flip__label">{card.back_title}</div>
                <div className="vc-flip__text">{card.back_body}</div>
                <div className="vc-flip__hint">tap to flip back ↩</div>
              </div>

              {/* Flip wrapper — absolutely fills the spacer-sized container */}
              <div className="vc-flip__inner">
                {/* Front */}
                <div className="vc-flip__face" aria-hidden={isFlipped}>
                  <div className="vc-flip__label">{card.front_title}</div>
                  <div>
                    <div className="vc-flip__value">{card.front_value}</div>
                    <div className="vc-small">{card.front_label}</div>
                  </div>
                  <div className="vc-flip__hint">tap for insight →</div>
                </div>

                {/* Back */}
                <div className="vc-flip__face vc-flip__face--back" aria-hidden={!isFlipped}>
                  <div>
                    <div className="vc-flip__label">{card.back_title}</div>
                    <div className="vc-flip__text" style={{ marginTop: 8 }}>{card.back_body}</div>
                  </div>
                  <div className="vc-flip__hint">tap to flip back ↩</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StepResults({ inputs }) {
  const { results, stampDuty, lct, exitValue, runPerYear } = useMemo(
    () => calcResults(inputs),
    [inputs]
  );

  /* Shared page-level disclosure depth for the bento result cards. */
  const [depth, setDepth] = useState(1);

  const hasResults = results.length > 0;
  const winner = hasResults ? results[0] : null;
  const savings = hasResults && results.length > 1
    ? results[results.length - 1].totalCost - results[0].totalCost
    : 0;

  return (
    <>
      {/* Summary strip */}
      <div className="vc-readings vc-readings--three">
        {[
          { label: "Drive-away", value: fmt(inputs.vehiclePrice) },
          { label: "Stamp duty (incl.)", value: fmt(stampDuty) },
          { label: "LCT", value: lct > 0 ? fmt(lct) : "Nil" },
          { label: "Est. exit value", value: fmt(exitValue) },
          { label: "Running / yr", value: fmt(runPerYear) },
        ].map(s => <Stat key={s.label} label={s.label} value={s.value} />)}
      </div>

      {/* Winner banner */}
      {winner && savings > 0 && (
        <div className="vc-card vc-result">
          <div className="vc-result__head">
            <div className="vc-result__headline">
              <p className="vc-label">Lowest true cost — not a recommendation</p>
              <p className="vc-pcard__title">{winner.label}</p>
            </div>
            <div className="vc-result__headline">
              <p className="vc-label">Modelled saving vs highest</p>
              <p className="vc-figure" style={{ fontSize: 28, lineHeight: 1.1 }}>{fmt(savings)}</p>
            </div>
          </div>
          <p className="vc-result__message">
            {(() => {
              const loser = results[results.length - 1];
              const intDiff = (loser.totalInterest || 0) - (winner.totalInterest || 0);
              if (winner.method === "novated") {
                return `${winner.label} shows the lowest total because pre-tax salary sacrifice saves ${fmt(winner.taxSavingMonthly)}/mo in income tax${(winner.fbtExempt && inputs.isEV) ? " and your EV is FBT-exempt" : ""}. Over ${inputs.holdYears} years this adds up to ${fmt(winner.taxSavingMonthly * 12 * Math.min(inputs.holdYears, inputs.novatedTerm))} in tax savings compared to after-tax alternatives.`;
              } else if (winner.method === "cash") {
                return `${winner.label} shows the lowest total because there is no interest to pay. The opportunity cost of ${fmt(winner.opportunityCostTotal || 0)} is included in the figure. If your opportunity cost rate is higher, financing could narrow or reverse this gap.`;
              } else if (intDiff > 500) {
                return `${winner.label} shows the lowest total largely because it carries ${fmt(Math.abs(intDiff))} less interest than ${loser.label} over the full term. Lower interest rate or shorter term both reduce this cost.`;
              } else {
                return `${winner.label} shows the lowest total cost over the ${inputs.holdYears}-year hold period. All figures include stamp duty, running costs, and estimated exit value. General information only.`;
              }
            })()}
          </p>
        </div>
      )}

      {/* Depth-controlled results: shared page depth drives the winner hero
          and the ranked runner-up rows. */}
      {hasResults && (() => {
        const ctx = { exitValue, runPerYear, holdYears: inputs.holdYears, stampDuty, state: inputs.state };
        const heroSummary = {
          title: winner.label,
          total: winner.totalCost,
          monthly: winner.effectiveMonthly,
          savingVs: results.length > 1
            ? { amount: results[1].totalCost - winner.totalCost, label: "next best" }
            : null,
        };
        const runnersUp = results.slice(1);
        const openAll = () => setDepth(2);
        return (
          <div className="vc-calc__block">
            <DepthControl depth={depth} onChange={setDepth} stickyOnMobile={false} />
            <ProgressiveCard
              featured
              expandable
              depth={depth}
              items={buildQuickCompareItems(winner, ctx)}
              summary={heroSummary}
              onDrill={openAll}
              titleAs="h3"
            />
            {runnersUp.length > 0 && (
              <div className="vc-calc__block" style={{ gap: 10 }}>
                <p className="vc-small">
                  The other {runnersUp.length === 1 ? "option" : `${runnersUp.length} options`}, ranked{depth === 1 ? " — tap to open" : ""}
                </p>
                {runnersUp.map((r) => {
                  const summary = {
                    title: r.label,
                    total: r.totalCost,
                    monthly: r.effectiveMonthly,
                    savingVs: { amount: -(r.totalCost - winner.totalCost), label: "best" },
                  };
                  const items = buildQuickCompareItems(r, ctx);
                  if (depth === 1) {
                    /* Pure one-line row; tapping advances the page depth so
                       every row becomes an expandable card. */
                    return (
                      <div
                        key={r.method}
                        className="vc-pcard-link"
                        role="button"
                        tabIndex={0}
                        aria-label={`Open breakdown for ${r.label}`}
                        onClick={openAll}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openAll(); } }}
                      >
                        <ProgressiveCard depth={1} expandable={false} items={items} summary={summary} />
                      </div>
                    );
                  }
                  return (
                    <ProgressiveCard key={r.method} depth={depth} expandable items={items} summary={summary} titleAs="h3" />
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {!hasResults && (
        <p className="vc-small" style={{ textAlign: "center", padding: 32 }}>
          Please go back and select at least one finance option to compare.
        </p>
      )}

      {/* ── Insight flip cards ─────────────────────────────────────────── */}
      {hasResults && <InsightCards results={results} inputs={inputs} />}

      {/* O2 cross-calc parity panel — same inputs, different lens. The links
          encode the user's inputs into the destination calculator's URL hash. */}
      {inputs.includeNovated && hasResults && (
        <div className="vc-card vc-calc__block">
          <h3 className="vc-checks__option">Want a deeper novated lease view?</h3>
          <p className="vc-small">The dedicated /novated tool shows FBT step-by-step, fortnightly take-home impact, and the residual table — same scenario, more detail.</p>
          <a className="vc-btn" href={buildNovatedUrl(inputs)}>
            Open in novated calculator
          </a>
          <p className="vc-small">{countCustomQCInputs(inputs)}</p>
        </div>
      )}

      {/* CTA to full calculator */}
      <div className="vc-band">
        <div style={{ flex: "1 1 320px", display: "flex", flexDirection: "column", gap: 4 }}>
          <h3 className="vc-checks__option">Want the full picture?</h3>
          <p className="vc-small" style={{ margin: 0 }}>The full Veercal calculator adds 6 analysis tabs — cash flow, equity, exit simulator, deep dive, on-costs, and year-by-year data.</p>
        </div>
        <a className="vc-btn vc-btn--accent" href={buildCalcUrl(inputs)}>
          Open Full Calculator
        </a>
      </div>

      <p className="vc-fine">
        General information only — not financial advice. All figures are indicative estimates based on the inputs provided and simplified assumptions. Rates verified {ONCOSTS_META.lastReviewed}. Always consult a licensed financial adviser before making financial decisions.
      </p>
    </>
  );
}

/* ─── URL state bridge: encode QC inputs for full calculator ────────────── */
function buildCalcUrl(inputs) {
  /* Maps QuickCompare input keys to the full calculator's DEFAULTS keys,
     encodes as base64 JSON in the URL hash — same format the full calculator uses */
  try {
    const mapped = {
      vehiclePrice: inputs.vehiclePrice,
      state: inputs.state,
      isEV: inputs.isEV,
      evType: inputs.evType,
      holdYears: inputs.holdYears,
      annualKm: inputs.annualKm,
      loanDeposit: inputs.deposit,
      loanRate: inputs.loanRate,
      loanTermYears: inputs.loanTerm,
      dealerRate: inputs.dealerRate,
      dealerTermYears: inputs.dealerTerm,
      dealerDeposit: inputs.dealerDeposit,
      balloonPct: 0.25,
      grossSalary: inputs.salary,
      novatedRate: inputs.novatedRate,
      novatedTermYears: inputs.novatedTerm,
      fuelPerL: inputs.fuelPerL,
      fuelL100km: inputs.fuelL100km,
      evEfficiencyKwh: inputs.evEffKwh,
      evHomeChargeKwh: inputs.evChargeRate,
      tyresPerYear: inputs.tyres,
      servicesPerYear: inputs.service,
      insurancePerYear: inputs.insurance,
      regPerYear: inputs.rego,
      depYr1: inputs.dep1,
      depYr2: inputs.dep2,
      depYrN: inputs.depN,
      opportunityCostPct: inputs.opportunityCost,
    };
    const encoded = btoa(JSON.stringify(mapped));
    return `/calculator#s:${encoded}`;
  } catch {
    return "/calculator";
  }
}

/* O2: same idea but encodes into /novated's URL-hash format. /novated uses
   the field-id-keyed shape that its own collectShareState produces. */
/* Pre-flight badge text — counts how many fields differ from QC's INITIAL
   defaults so the cross-link can promise "X custom inputs preserved". */
function countCustomQCInputs(inputs) {
  const fields = ['vehiclePrice', 'state', 'isEV', 'evType', 'annualKm', 'holdYears',
    'deposit', 'dealerDeposit', 'loanRate', 'loanTerm', 'dealerRate', 'dealerTerm',
    'salary', 'novatedRate', 'novatedTerm', 'insurance', 'rego', 'service', 'tyres'];
  let custom = 0;
  for (const k of fields) {
    if (inputs[k] === undefined || INITIAL[k] === undefined) continue;
    const a = inputs[k], b = INITIAL[k];
    if (typeof a === 'number' && typeof b === 'number') {
      if (Math.abs(a - b) > 0.0001) custom++;
    } else if (a !== b) custom++;
  }
  return custom === 0
    ? 'Running with defaults — same scenario, more detail'
    : custom + ' custom input' + (custom === 1 ? '' : 's') + ' preserved';
}

function buildNovatedUrl(inputs) {
  try {
    const vType = inputs.isEV ? (inputs.evType === 'phev' ? 'phev' : 'ev') : 'ice';
    /* /novated takes fuel as a literal $/yr input, whereas QC computes it
       from km × L/100 × $/L (or km × kWh × $/kWh for EVs). Compute QC's
       fuel cost forward and pass it as /novated's fuel field. */
    const km = Math.max(1, inputs.annualKm || 15000);
    const annualFuel = inputs.isEV
      ? (() => {
          const eff       = inputs.evEffKwh ?? 18;
          const homeRate  = inputs.evChargeRate ?? 0.28;
          const homePct   = inputs.evPctHomeCharge ?? 1;
          const pubRate   = inputs.evPublicChargeKwh ?? homeRate;
          const totalKwh  = (km / 100) * eff;
          return totalKwh * homePct * homeRate + totalKwh * (1 - homePct) * pubRate;
        })()
      : (km / 100) * (inputs.fuelL100km ?? 9) * (inputs.fuelPerL ?? 2.10);
    const novatedState = {
      salary:        String(inputs.salary || 100000),
      state:         inputs.state,
      leaseTerm:     String(inputs.novatedTerm || 3),
      vehiclePrice:  String(inputs.vehiclePrice || 55000),
      annualKm:      String(inputs.annualKm || 15000),
      rate:          String((inputs.novatedRate || 0.065) * 100),
      fuel:          String(Math.round(annualFuel)),
      insurance:     String(inputs.insurance || 1800),
      rego:          String(inputs.rego || 900),
      service:       String(inputs.service || 800),
      tyres:         String(inputs.tyres || 600),
      vType,
    };
    const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(novatedState))));
    return `/novated#s=${encoded}`;
  } catch {
    return "/novated";
  }
}

/* ─── URL hash: decode any shared scenario on initial load ─────────────────
   Format: #s=<base64-encoded JSON>. Mirrors the static-page pattern. */
function getInitialQCInputs() {
  try {
    const h = typeof window !== 'undefined' ? (window.location.hash || '') : '';
    if (h.startsWith('#s=')) {
      const decoded = JSON.parse(decodeURIComponent(escape(atob(h.slice(3)))));
      track('load_scenario_url', { surface: 'quick_compare' });
      return { ...INITIAL, ...decoded };
    }
  } catch (e) { /* fall through to defaults */ }
  return INITIAL;
}

/* Thin gtag wrapper — silently no-ops if gtag isn't available. */
function track(eventName, params = {}) {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', eventName, params);
    }
  } catch {}
}

/* ─── Main component ─────────────────────────────────────────────────────── */
export default function QuickCompare() {
  const [step, setStep] = useState(0);
  const [inputs, setInputs] = useState(getInitialQCInputs);
  const [shareToast, setShareToast] = useState(false);

  const upd = (key, val) => setInputs(p => ({ ...p, [key]: val }));

  const shareScenario = () => {
    try {
      const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(inputs))));
      const url = `${window.location.origin}${window.location.pathname}#s=${encoded}`;
      window.history.replaceState(null, '', `#s=${encoded}`);
      navigator.clipboard?.writeText(url).catch(() => {});
      setShareToast(true);
      setTimeout(() => setShareToast(false), 2500);
      track('share_scenario', { surface: 'quick_compare', state: inputs.state, isEV: !!inputs.isEV });
    } catch (e) { /* ignore */ }
  };

  const canAdvance = () => {
    if (step === 2 && !inputs.includeCash && !inputs.includeLoan && !inputs.includeDealer && !inputs.includeNovated) return false;
    if (step === 3 && inputs.includeNovated && inputs.salary <= 0) return false;
    return true;
  };

  const stepComponents = [
    <StepVehicle inputs={inputs} upd={upd} />,
    <StepUsage inputs={inputs} upd={upd} />,
    <StepFinance inputs={inputs} upd={upd} />,
    <StepDetails inputs={inputs} upd={upd} />,
    <StepResults inputs={inputs} />,
  ];

  const isLastStep = step === STEPS.length - 1;

  return (
    <>
      <a className="vc-skip" href="#main">Skip to content</a>

      {/* The v2 header every page uses (src/design/v2/SiteChrome.jsx). */}
      <SiteHeader current="compare" />

      <main id="main" className="vc-container">
        <div className="vc-calc-head">
          <h1 className="vc-h1">
            Quick Compare
          </h1>
          <p className="vc-lead">
            Answer five quick questions and get an instant side-by-side comparison of every car finance option.
          </p>
        </div>

        <div className="vc-wizard" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Progress bar — an amount, so amber on tint. The step line below says it in words. */}
          <div className="vc-progress" aria-hidden="true">
            <div className="vc-progress__bar" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
          </div>

          {/* Step indicator — done steps go back */}
          <ol className="vc-stepper">
            {STEPS.map((s, i) => (
              <li key={s.id}>
                <button
                  type="button"
                  className={i < step ? "is-done" : undefined}
                  aria-current={i === step ? "step" : undefined}
                  disabled={i > step}
                  onClick={() => i < step && setStep(i)}
                >
                  {s.title}
                </button>
              </li>
            ))}
          </ol>

          {/* Card */}
          <div className="vc-card vc-wizard">
            <div className="vc-wizard__head">
              <div>
                <p className="vc-label">
                  Step {step + 1} of {STEPS.length}
                </p>
                <h2 className="vc-h3">
                  {STEPS[step].title}
                </h2>
                <p className="vc-small">{STEPS[step].desc}</p>
              </div>
              <button
                type="button"
                className="vc-btn"
                onClick={shareScenario}
                title="Copy a link to your scenario"
              >
                {shareToast ? "Link copied!" : "Share"}
              </button>
            </div>

            <div className="vc-wizard__body">
              {stepComponents[step]}
            </div>

            {/* Navigation */}
            <div className="vc-wizard__foot">
              <button
                type="button"
                className="vc-btn"
                onClick={() => setStep(s => Math.max(0, s - 1))}
                disabled={step === 0}
              >
                Back
              </button>

              {!isLastStep ? (
                <button
                  type="button"
                  className="vc-btn vc-btn--primary"
                  onClick={() => {
                    if (!canAdvance()) return;
                    const next = step + 1;
                    if (next === STEPS.length - 1) {
                      /* Fired once when the user lands on Results — this is the
                         conversion event for the guided flow. */
                      track('qc_view_results', { state: inputs.state, isEV: !!inputs.isEV, includeNovated: !!inputs.includeNovated });
                    }
                    setStep(next);
                  }}
                  disabled={!canAdvance()}
                >
                  {step === STEPS.length - 2 ? "See my comparison" : "Next"}
                </button>
              ) : (
                <button
                  type="button"
                  className="vc-btn"
                  onClick={() => { setStep(0); setInputs(INITIAL); }}
                >
                  Start over
                </button>
              )}
            </div>
          </div>
        </div>
      </main>

      <SiteFooter />
    </>
  );
}
